import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";

// Pages & Components
import CookieBanner from "./components/CookieBanner";
import OcrReview from "./pages/admin/OcrReview";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPasswordForm from "./components/ForgotPasswordForm";
import Onboarding from "./pages/Onboarding";
import BankRedirect from "./pages/BankRedirect";
import Settings from "./pages/Settings";
import Privacy from "./pages/Privacy";
import Contact from "./pages/Contact";
import InvoiceForm from "./pages/InvoiceForm";
import InvoicesPage from "./pages/InvoicesPage";
import CancelledPage from "./pages/CancelledPage";
import Dashboard from "./pages/Dashboard";
import DashboardHome from "./pages/DashboardHome";
import PricingPage from "./pages/PricingPage";
import Home from "./pages/Home";
import ReceiptUploader from "./components/ReceiptUploader";
import Inbox from "./components/Inbox";
import MonthlySummary from "./components/MonthlySummary";
import OcrDebugPanel from "./components/OcrDebugPanel";
import ClientsPage from "./components/clients/ClientsPage";
import Terms from "./pages/Terms";
import AdminDashboard from "./pages/AdminPage";
import AdminUserDetails from "./pages/admin/AdminUserDetails";
import Payroll from "./pages/Payroll";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ProjectsPage from "./pages/ProjectsPage";
import VATReturnsPage from "./pages/VATReturnsPage";
import DirectorDashboard from "./components/DirectorDashboard";
import RevenueAccountsPage from "./pages/RevenueAccountsPage";
import DirectorReviewPage from "./pages/DirectorReviewPage";

// Accountant-specific
import AccountantSignup from "./pages/AccountantSignup";
import AccountantDashboard from "./components/accountant/AccountantDashboard";
import ClientProfileView from "./components/accountant/ClientProfileView";
import SelectProvider from "./pages/accountant/SelectProvider";
import AccountantOnboarding from "./pages/accountant/AccountantOnboarding";
import PricingSelection from "./pages/accountant/PricingSelection";
import BankTransactions from "./pages/BankTransactions";
import ReportsPage from "./pages/accountant/ReportsPage";
import VATPage from "./pages/accountant/VATPage";
import BankingPage from "./pages/accountant/BankingPage";
import BankFeedsPage from "./pages/accountant/BankFeedsPage";
import AccountantLayout from "./components/accountant/AccountantLayout";

// contexts
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";

function PrivateRoute({ user, children, requireAdmin = false }) {
  const isAdmin = user?.email === "devabyltd@gmail.com";
  if (!user) return <Navigate to="/login" />;
  if (requireAdmin && !isAdmin) return <Navigate to="/dashboard" />;
  return children;
}

function AppRoutes() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        const docRef = doc(db, "users", u.uid);
        const snap = await getDoc(docRef);
        const role = snap.exists() ? snap.data().role : "freelancer";
        setUser(u);

        if (
          window.location.pathname === "/" ||
          window.location.pathname === "/login"
        ) {
          if (role === "accountant") {
            navigate("/accountant");
          } else {
            navigate("/dashboard");
          }
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsub;
  }, [navigate]);

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/accountant-signup" element={<AccountantSignup />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordForm />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/cancelled" element={<CancelledPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />

        {/* Accountant routes wrapped in shared layout */}
        <Route
          path="/accountant"
          element={
            <PrivateRoute user={user}>
              <AccountantLayout>
                <AccountantDashboard />
              </AccountantLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/accountant/pricing"
          element={
            <PrivateRoute user={user}>
              <AccountantLayout>
                <PricingSelection />
              </AccountantLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/accountant/onboarding"
          element={
            <PrivateRoute user={user}>
              <AccountantLayout>
                <AccountantOnboarding />
              </AccountantLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/accountant/select-provider"
          element={
            <PrivateRoute user={user}>
              <AccountantLayout>
                <SelectProvider />
              </AccountantLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/accountant/client/:uid"
          element={
            <PrivateRoute user={user}>
              <AccountantLayout>
                <ClientProfileView />
              </AccountantLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/accountant/banking"
          element={
            <PrivateRoute user={user}>
              <AccountantLayout>
                <BankingPage />
              </AccountantLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/accountant/bank-feeds"
          element={
            <PrivateRoute user={user}>
              <AccountantLayout>
                <BankFeedsPage />
              </AccountantLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/accountant/vat"
          element={
            <PrivateRoute user={user}>
              <AccountantLayout>
                <VATPage />
              </AccountantLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/accountant/reports"
          element={
            <PrivateRoute user={user}>
              <AccountantLayout>
                <ReportsPage />
              </AccountantLayout>
            </PrivateRoute>
          }
        />

        {/* Main app routes */}
        <Route
          path="/dashboard/debug"
          element={<OcrDebugPanel user={user} />}
        />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute user={user}>
              <Dashboard user={user} />
            </PrivateRoute>
          }
        >
          <Route index element={<DashboardHome user={user} />} />
          <Route path="upload" element={<ReceiptUploader />} />
          <Route path="inbox" element={<Inbox />} />
          <Route path="summary" element={<MonthlySummary />} />
          <Route path="settings" element={<Settings />} />
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="invoices/new" element={<InvoiceForm />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="clients" element={<ClientsPage />} />
          <Route path="payroll" element={<Payroll />} />
          <Route path="bank-transactions" element={<BankTransactions />} />
          <Route path="bank-redirect" element={<BankRedirect />} />
          <Route path="vat" element={<VATReturnsPage />} />
          <Route path="director" element={<DirectorDashboard />} />
          <Route path="accounts" element={<RevenueAccountsPage />} />
          <Route path="review" element={<DirectorReviewPage />} />
        </Route>

        <Route
          path="/onboarding"
          element={
            <PrivateRoute user={user}>
              <Onboarding />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <PrivateRoute user={user} requireAdmin>
              <AdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/user/:uid"
          element={
            <PrivateRoute user={user} requireAdmin>
              <AdminUserDetails />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/ocr-review"
          element={
            <PrivateRoute user={user} requireAdmin>
              <OcrReview />
            </PrivateRoute>
          }
        />
      </Routes>

      <CookieBanner />
    </>
  );
}

export default function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}
